import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/utils/api';
import { setAuth } from '@/utils/auth';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const session_id = params.get('session_id');

        if (!session_id) {
          toast.error('Invalid authentication response');
          navigate('/login');
          return;
        }

        // Exchange session_id for user session
        const response = await api.post('/auth/oauth/session', {}, {
          headers: {
            'X-Session-ID': session_id
          },
          withCredentials: true  // Important: Send cookies
        });

        // Mandatory 2FA: OAuth never issues a session directly anymore. Route
        // to the /two-factor page to enroll or verify Google Authenticator.
        if (response.data?.requires_totp_enrollment || response.data?.requires_totp) {
          sessionStorage.setItem('safer_mfa_token', response.data.mfa_token);
          sessionStorage.setItem('safer_mfa_mode', response.data.requires_totp_enrollment ? 'enroll' : 'challenge');
          navigate('/two-factor', { replace: true });
          return;
        }

        // Use unified auth utility to store token and user
        setAuth(response.data.access_token, response.data);
        
        // Check for return URL
        const returnUrl = sessionStorage.getItem('safer_return_url');
        sessionStorage.removeItem('safer_return_url');
        
        // Navigate to dashboard or return URL
        navigate(returnUrl || '/dashboard', { 
          replace: true,
          state: { user: response.data }
        });
        
        toast.success(`Welcome back, ${response.data.display_name || response.data.full_name}!`);
      } catch (error) {
        console.error('OAuth callback error:', error);
        const errorMessage = error.response?.data?.detail || 'Authentication failed';
        toast.error(errorMessage);
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
